import type { ToolContent } from "./tool-content";

const genericEsContent = (name: string, action: string): ToolContent => ({
  seoTitle: `${name} Gratis Online – Herramienta Fácil e Interactiva | Picamini.online`,
  seoDescription: `Herramienta gratuita online de ${name.toLowerCase()}. ${action} 100% gratis, sin registro ni instalaciones. Funciona en móviles, tabletas y proyectores escolares.`,
  h1Title: `${name} Gratis Online – Rápido, Justo e Interactivo`,
  intro: `${action} Diseñado especialmente para docentes, estudiantes, organizadores de eventos y facilitadores. Todo se ejecuta de forma 100% privada y local en tu navegador web sin registro previo.`,
  whatIs: [
    `El ${name} es una aplicación web gratuita diseñada para hacer que las elecciones, dinámicas de aula y sorteos de grupo sean rápidos, imparciales y muy vistosos.`,
    `Tanto si organizas una clase escolar como si gestionas una reunión virtual o una noche de juegos, esta herramienta sustituye el papel y los sorteos manuales por algoritmos visuales inmediatos. Cuenta con tipografía de alto contraste, controles intuitivos y efectos de sonido ajustables.`,
    `A diferencia de otras plataformas lentas o llenas de anuncios molestos, nuestro ${name.toLowerCase()} no requiere crear cuentas de usuario ni instalar programas. Solo abre la página web y empieza a usarlo inmediatamente.`
  ],
  howTo: [
    `Abre la herramienta de ${name} desde cualquier navegador web en ordenador, tableta o smartphone.`,
    "Ingresa o modifica la lista de nombres, participantes u opciones numéricas en el panel de configuración.",
    "Ajusta tus preferencias, como activar o desactivar el sonido, cambiar el número de selecciones o elegir el modo de visualización.",
    "Haz clic en el botón principal para activar la selección aleatoria e imparcial.",
    "Observa la animación en pantalla y escucha los efectos de sonido mientras se revela el resultado.",
    "Utiliza los botones de copiar o guardar para exportar el resultado o compartirlo con tu grupo."
  ],
  features: [
    "100% Gratuito y sin límites de uso diario ni marcas de agua",
    "Sin registro obligatorio ni necesidad de ingresar datos personales",
    "Procesamiento local en el navegador: tus listas y datos nunca salen de tu dispositivo",
    "Modo de presentación de alta visibilidad ideal para pizarras digitales y proyectores",
    "Efectos de sonido interactivos con opción de silencio en un clic",
    "Navegación accesible mediante teclado para un uso rápido",
    "Diseño adaptable y fluido para iPhone, iPad, Android y ordenadores",
    "Opciones de copia e impresión directa para registros del aula"
  ],
  benefits: [
    "Ahorra tiempo valioso en la preparación de clases y eventos",
    "Garantiza absoluta imparcialidad matemática y evita acusaciones de favoritismo",
    "Aumenta la participación y atención del público mediante estímulos visuales",
    "Funciona de manera estable incluso con conexiones a internet lentas una vez cargada la página",
    "Interfaz limpia y libre de distracciones publicitaria agresiva"
  ],
  proTips: [
    "Proyecta la pantalla en la pizarra digital del aula para que todos los estudiantes sigan el sorteo en directo.",
    "Activa la opción de 'Eliminar seleccionado' para asegurar que todos los alumnos participen antes de repetir.",
    "Guarda tus listas personalizadas marcando la página en marcadores; tu lista se guarda automáticamente en el navegador.",
    "Utiliza la opción de copiar para enviar rápidamente los grupos o ganadores al chat de tu reunión."
  ],
  useCases: [
    {
      role: "Docentes y Profesores de Educación Primaria y Secundaria",
      description: "Mantén el entusiasmo en el aula mediante selecciones de alumnos imparciales, creación de grupos equilibrados y temporizadores visuales."
    },
    {
      role: "Animadores, Facilitadores y Coordinadores de Eventos",
      description: "Dinamiza reuniones de equipo, talleres de formación, juegos de mesa y fiestas con sorteos de premios y preguntas de debate al instante."
    }
  ],
  detailedGuide: [
    {
      heading: `Guía Completa para Aprovechar al Máximo el ${name}`,
      paragraphs: [
        `En entornos educativos y profesionales modernos, la imprevisibilidad justa y la transparencia son fundamentales para mantener la atención. La herramienta de ${name.toLowerCase()} ofrece una solución digital probada que elimina el sesgo humano en las decisiones cotidianas.`,
        `Nuestra plataforma utiliza algoritmos de generación de números aleatorios criptográficamente seguros en el propio navegador, garantizando que cada elemento o participante tenga exactamente la misma probabilidad matemática de resultar elegido.`
      ],
      bulletPoints: [
        "Cero tiempo de espera: resultados instantáneos en pantalla",
        "Privacidad de datos total: la información permanece en la memoria de tu navegador",
        "Compatibilidad multiplataforma sin instalar aplicaciones externas"
      ]
    }
  ],
  comparisonTable: {
    title: "Comparativa: Picamini.online frente a Métodos Tradicionales",
    headers: ["Característica", "Picamini.online", "Sorteo Tradicional de Papel"],
    rows: [
      {
        feature: "Tiempo de preparación",
        pickamini: "Instantáneo (0 segundos)",
        competitor: "Varios minutos recortando papel",
        paper: "Lento"
      },
      {
        feature: "Imparcialidad",
        pickamini: "100% Algorítmica y Transparente",
        competitor: "Sujeta a pliegues o sesgos visuales",
        paper: "Dudosa"
      },
      {
        feature: "Visibilidad en grupo",
        pickamini: "Excelente en pantallas y proyectores",
        competitor: "Difícil de ver desde lejos",
        paper: "Baja"
      }
    ]
  },
  ideasAndTemplates: [
    {
      title: "Uso en Turnos de Participación",
      description: "Utiliza la herramienta al comenzar la clase para seleccionar al encargado de repasar la lección anterior o responder preguntas de calentamiento.",
      exampleItems: ["Turno de lectura", "Presentación de ejercicios", "Repaso de tarea"]
    },
    {
      title: "Juegos y Dinámicas de Integración",
      description: "Aplica el selector en actividades de integración para asignar roles al azar o formar parejas de trabajo colaborativo de manera espontánea.",
      exampleItems: ["Trabajo en parejas", "Debates grupales", "Juegos de preguntas"]
    }
  ],
  probabilityMath: {
    title: "Principios Matemáticos de la Selección Aleatoria",
    formula: "P(A) = 1 / N",
    explanation: "Cada elemento de tu lista tiene una probabilidad uniforme idéntica (P) de ser seleccionado, donde N representa el número total de opciones válidas ingresadas.",
    keyConcepts: [
      {
        concept: "Equiprobabilidad",
        detail: "Todos los elementos poseen exactamente la misma oportunidad de selección en cada intento."
      },
      {
        concept: "Independencia de Eventos",
        detail: "Salvo que actives la eliminación de ganadores, cada tirada es independiente de los resultados anteriores."
      }
    ]
  },
  faq: [
    {
      q: `¿Es gratis usar la herramienta de ${name}?`,
      a: "Sí, es 100% gratuita y sin límites. No necesitas crear una cuenta, pagar suscripciones ni ingresar tarjetas de crédito."
    },
    {
      q: "¿Mis datos o listas de alumnos se guardan en algún servidor?",
      a: "No. Toda la información que ingresas se procesa de forma estrictamente local en la memoria de tu navegador por razones de privacidad y seguridad."
    },
    {
      q: "¿Puedo proyectar la pantalla en el aula?",
      a: "¡Por supuesto! El diseño está optimizado con tipografías grandes y alto contraste para proyectarse perfectamente en pizarras digitales, televisores y pantallas."
    }
  ]
});

// Specific overrides for key tools to maximize native search relevance
const ES_TOOL_OVERRIDES: Record<string, Partial<ToolContent>> = {
  "selector-de-nombres": {
    seoTitle: "Selector de Nombres Aleatorios Gratis – Sorteo Rápido | Picamini.online",
    seoDescription: "Selector de nombres aleatorios online gratis. Sacar nombres al azar para clases, sorteos y eventos. Sin registro y 100% privado en el navegador.",
    h1Title: "Selector de Nombres Aleatorios Gratis – Transparente y Rápido",
    intro: "Elige participantes, estudiantes o ganadores al azar de forma justa con animación rápida y efectos visuales. Ideal para profesores, dinámicas de grupo y sorteos online.",
  },
  "selector-de-alumnos": {
    seoTitle: "Selector Aleatorio de Alumnos Gratis – Para Profesores y Aulas | Picamini.online",
    seoDescription: "Selector aleatorio de alumnos gratis para docentes. Ingresa la lista de tu clase y elige un estudiante al azar con historial y sin repetir.",
    h1Title: "Selector Aleatorio de Alumnos para el Aula",
    intro: "Diseñado para mantener la equidad y atención en la clase. Ingresa la lista de estudiantes, activa la selección aleatoria e involucra a todos de forma neutral.",
  },
  "ruleta-aleatoria": {
    seoTitle: "Ruleta Aleatoria Online Gratis – Girar la Ruleta | Picamini.online",
    seoDescription: "Ruleta aleatoria online gratis para tomar decisiones y sortear opciones. Personaliza nombres, gira la ruleta interactiva y elige un ganador al instante.",
    h1Title: "Ruleta Aleatoria Online – Gira y Toma Decisiones al Instante",
    intro: "Una vistosa ruleta giratoria interactiva para sortear opciones, nombres, actividades o decisiones. Personaliza los sectores y disfruta de efectos visuales fluidos.",
  },
  "ruleta-de-nombres": {
    seoTitle: "Ruleta de Nombres Gratis – Sorteo de Nombres en Ruleta | Picamini.online",
    seoDescription: "Ruleta de nombres online gratis. Ingresa tu lista de nombres, gira la ruleta de la suerte y selecciona un ganador de forma vistosa y divertida.",
    h1Title: "Ruleta de Nombres Online – Sorteos Imparciales y Divertidos",
    intro: "Escribe o pega tus nombres, gira la ruleta colorida y deja que el azar elija al ganador con animación de celebración y confeti.",
  },
  "ruleta-si-o-no": {
    seoTitle: "Ruleta del Sí o No Gratis – Girar Ruleta 50/50 | Picamini.online",
    seoDescription: "Ruleta del Sí o No online. Toma decisiones rápidas de 50/50 girando nuestra ruleta interactiva. Gratis, instantánea y fácil de usar.",
    h1Title: "Ruleta del Sí o No – Toma Decisiones Rápidas",
    intro: "¿Tienes dudas? Gira la ruleta del Sí o No para obtener una respuesta inmediata al azar y resolver dilemas divertidos.",
  },
  "ruleta-de-premios": {
    seoTitle: "Ruleta de Premios y Sorteos Gratis | Picamini.online",
    seoDescription: "Ruleta de premios online gratis para sorteos escolares, promociones y eventos. Personaliza los incentivos y gira para entregar recompensas.",
    h1Title: "Ruleta de Premios y Recompensas para Clase y Eventos",
    intro: "Motiva a tus alumnos o asistentes con una ruleta de premios atractiva. Añade recompensas como diplomas, puntos extra o pequeños detalles y gira al instante.",
  },
  "generador-de-equipos": {
    seoTitle: "Generador de Equipos Aleatorios Gratis – Dividir Grupos | Picamini.online",
    seoDescription: "Generador de equipos aleatorios online gratis. Divide listas de alumnos o personas en equipos equilibrados al instante en 1 solo clic.",
    h1Title: "Generador de Equipos Aleatorios – Equilibrado y Rápido",
    intro: "Arma equipos de trabajo o juegos sin discusiones. Pega los nombres de los integrantes, selecciona la cantidad de equipos deseada y genera listas niveladas al instante.",
  },
  "creador-de-grupos": {
    seoTitle: "Creador de Grupos Aleatorios Gratis | Picamini.online",
    seoDescription: "Creador de grupos aleatorios gratis para aulas y talleres. Organiza personas por tamaño de grupo o cantidad de grupos en segundos.",
    h1Title: "Creador de Grupos Aleatorios para Profesores y Equipos",
    intro: "Crea subgrupos aleatorios en segundos. Elige si prefieres indicar la cantidad total de grupos o el número máximo de participantes por mesa.",
  },
  "generador-de-parejas": {
    seoTitle: "Generador de Parejas Aleatorias Gratis | Picamini.online",
    seoDescription: "Generador de parejas aleatorias gratis. Forma parejas al azar para trabajos escolares o actividades grupales de forma equitativa.",
    h1Title: "Generador de Parejas Aleatorias al Instante",
    intro: "Asigna compañeros de trabajo en parejas de forma neutral. Gestiona automáticamente números impares creando un trío si es necesario.",
  },
  "dado-virtual": {
    seoTitle: "Dado Virtual Online Gratis – Lanzar Dados | Picamini.online",
    seoDescription: "Dado virtual online gratis de 6, 8, 10, 12, 20 o 100 caras. Tirar dados interactivos con animación 3D y sonido realista.",
    h1Title: "Dado Virtual Online – Lanza Dados de Cualquier Cara",
    intro: "¿Olvidaste tus dados físicos? Lanza dados virtuales para juegos de mesa, rol, clases de matemáticas y probabilidades con animación realista.",
  },
  "cara-o-cruz": {
    seoTitle: "Lanzar Moneda Online – Cara o Cruz Gratis | Picamini.online",
    seoDescription: "Lanzar moneda online gratis. Elige cara o cruz tirando una moneda virtual en 3D con resultados impredecibles al instante.",
    h1Title: "Lanzar Moneda Online – Juego de Cara o Cruz",
    intro: "Resuelve una duda o decide quién empieza tirando una moneda virtual al aire con movimiento fluido y sonido característico.",
  },
  "temporizador-para-clase": {
    seoTitle: "Temporizador para Clase Gratis – Cuenta Atrás Aula | Picamini.online",
    seoDescription: "Temporizador para clase gratis en pantalla completa. Cuenta atrás proyectable para actividades escolares, exámenes y descansos.",
    h1Title: "Temporizador y Cuenta Atrás para la Clase",
    intro: "Mantén el ritmo del aula con un temporizador gigante visible desde cualquier rincón. Ajusta minutos u horas fácilmente y proyecta sin distracciones.",
  },
  "plano-de-asientos": {
    seoTitle: "Organizador de Asientos del Aula Gratis | Picamini.online",
    seoDescription: "Organizador de asientos y distribución de clase al azar. Crea mapas de aula aleatorios en filas o grupos e imprímelos gratis.",
    h1Title: "Organizador de Asientos y Distribución del Aula",
    intro: "AsignaPupitres y mesas de forma rápida y equitativa. Genera esquemas de aula aleatorios para evitar distracciones e imprime la plantilla en un clic.",
  }
};

export function getEsToolContent(slug: string, fallbackName: string): ToolContent {
  const base = genericEsContent(fallbackName, `Herramienta interactiva para ${fallbackName.toLowerCase()}.`);
  const override = ES_TOOL_OVERRIDES[slug] || {};
  return {
    ...base,
    ...override,
  };
}
